import { gql } from "@apollo/client";
import { useCallback } from "react";

import {
  Card,
  CardHeader,
  CardContent,
  makeStyles,
  CircularProgress,
  Button
} from "@material-ui/core";

import LoadingIndicator from "src/components/LoadingIndicator";
import {
  campaignStart,
  campaignStartVariables
} from "src/graphql/generated/campaignStart";
import {
  getCampaign_campaign_startJob,
  getCampaign_campaign
} from "src/graphql/generated/getCampaign";
import { JobStatus } from "src/graphql/generated/globalTypes";
import useMutation from "src/util/useMutation";

import BandwidthVoiceWarning from "./BandwidthVoiceWarning";
import { useCampaignEditEvents } from "./CampaignEditorsProvider";

const useStyles = makeStyles(theme => ({
  card: {
    margin: `${theme.spacing(3)}px 0`
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center"
  }
}));

const START_CAMPAIGN = gql`
  mutation campaignStart($campaignId: ID!) {
    campaignStart(id: $campaignId) {
      id
      startJob {
        id
        type
        createTime
        startTime
        progress
        status
        result
        errorCode
        errorMessage
        errorDetails
      }
    }
  }
`;

function JobRunning({
  job
}: {
  job: getCampaign_campaign_startJob;
}): JSX.Element {
  return (
    <LoadingIndicator
      progress={job.progress}
      message={
        <>
          <span>Starting Campaign ({(job.progress * 100).toFixed(1)}%)...</span>
          <br />
          <em>You can leave and the process will continue in the background</em>
        </>
      }
    />
  );
}

function ValidationError({
  validationErrors
}: {
  validationErrors: JSX.Element[];
}): JSX.Element {
  const classes = useStyles();

  return (
    <Card className={classes.card}>
      <CardHeader title="Can't start campaign" />
      <CardContent>
        <ul>
          {validationErrors.map((msg, i) => (
            <li key={i}>{msg}</li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export default function StartCampaign({
  campaign,
  validationErrors
}: {
  campaign: getCampaign_campaign;
  validationErrors: JSX.Element[];
}): JSX.Element {
  const classes = useStyles();

  const [startCampaign, { loading: isSaving }] = useMutation<
    campaignStart,
    campaignStartVariables
  >(START_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not start campaign",
    variables: {
      campaignId: campaign.id
    },
    awaitRefetchQueries: true,
    refetchQueries: ["getCampaign"]
  });

  const sendEditEvent = useCampaignEditEvents();

  const handleStart = useCallback(async () => {
    await startCampaign();
    sendEditEvent();
  }, [startCampaign, sendEditEvent]);

  if (campaign.startJob?.status === JobStatus.DONE) {
    return <div />;
  }

  if (
    campaign.startJob?.status === JobStatus.QUEUED ||
    campaign.startJob?.status === JobStatus.RUNNING
  ) {
    return <JobRunning job={campaign.startJob} />;
  }

  if (validationErrors.length) {
    return <ValidationError validationErrors={validationErrors} />;
  }

  return (
    <>
      <h2>Start Campaign</h2>
      <BandwidthVoiceWarning campaign={campaign} />
      {campaign.startJob?.status === JobStatus.FAILED ? (
        <Card className={classes.card}>
          <CardHeader title="Error starting campaign" />
          <CardContent>
            <div>
              <p>Failed to start campaign: {campaign.startJob.errorMessage}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <p>Your campaign is ready to start!</p>
      )}
      <div className={classes.buttonContainer}>
        {isSaving ? <CircularProgress size={24} /> : null}
        <Button
          variant="contained"
          color="primary"
          disabled={isSaving}
          onClick={handleStart}
        >
          Start Campaign
        </Button>
      </div>
    </>
  );
}
